/* eslint-disable @typescript-eslint/no-explicit-any */
import { configureStore } from "@reduxjs/toolkit";
import createSagaMiddleware from "redux-saga";
import { persistStore, persistReducer, createMigrate } from "redux-persist";
import storage from "redux-persist/lib/storage"; // Defaults to localStorage for web
import rootSaga from "./rootSaga";
import rootReducer, { RootState } from "./rootReducers";
import { usersApi } from "./apis/usersApi";
import { companyApi } from "./apis/companyApi";
import { campaignApi } from "./apis/campaignApi";
import { metricsApi } from "./apis/metricsApi";
import { internalCampaignsMetricsApi } from "./apis/internalCampaignsMetricsApi";
import { internalCampaignsMergeApi } from "./apis/internalCampaignsMergeApi";
import { systemStatsApi } from "./apis/systemStatsApi";
import { creativeMetricsApi } from "./apis/creativeMetricsApi";
import { publisherMetricsApi } from "./apis/publisherMetricsApi";
import { dimensionMetricsApi } from "./apis/dimensionMetricsApi";
import { currencyApi } from "./apis/currencyApi";
import { internalCampaignApi } from "./apis/internalCampaignApi";
import { customEntitiesApi } from "./apis/customEntitiesApi";
import { insertionOrderApi } from "./apis/insertionOrderApi";
import { insertionOrderMarginApi } from "./apis/insertionOrderMarginApi";
import { spotifyApi } from "./apis/spotifyApi";
import { dv360Api } from "./apis/dv360Api";
import { hivestackApi } from "./apis/hivestackApi";
import { amazonApi } from "./apis/amazonApi";
import { asyncOperationsApi } from "./apis/asyncOperationsApi";
import { briefingApi } from "./apis/briefingApi";
import { dataModelApi } from "./apis/dataModelApi";
import { wipApi } from "./apis/wipApi";
import { tableViewsApi } from "./apis/tableViewsApi";
import { adminApi } from "./apis/adminApi";
import { ocApi } from "./apis/ocApi";
import { invoiceApi } from "./apis/invoiceApi";
import { associatedCampaignMarginApi } from "./apis/associatedCampaignMarginApi";
import { clientRegistryApi } from "./apis/clientRegistryApi";
import { commercialProposalApi } from "./apis/commercialProposalApi";
import { commercialDashboardApi } from "./apis/commercialDashboardApi";
import { costAnalysisApi } from "./apis/costAnalysisApi";
import { controlBoardApi } from "./apis/controlBoardApi";
import { mcpApi } from "./apis/mcpApi";
import { serviceBriefingApi } from "./apis/serviceBriefingApi";
import { clientPortalApi } from "./apis/clientPortalApi";

const blacklistedPaths = [
  usersApi.reducerPath,
  companyApi.reducerPath,
  campaignApi.reducerPath,
  metricsApi.reducerPath,
  internalCampaignsMetricsApi.reducerPath,
  internalCampaignsMergeApi.reducerPath,
  internalCampaignApi.reducerPath,
  systemStatsApi.reducerPath,
  creativeMetricsApi.reducerPath,
  publisherMetricsApi.reducerPath,
  dimensionMetricsApi.reducerPath,
  currencyApi.reducerPath,
  customEntitiesApi.reducerPath,
  insertionOrderApi.reducerPath,
  insertionOrderMarginApi.reducerPath,
  spotifyApi.reducerPath,
  dv360Api.reducerPath,
  hivestackApi.reducerPath,
  amazonApi.reducerPath,
  asyncOperationsApi.reducerPath,
  briefingApi.reducerPath,
  dataModelApi.reducerPath,
  wipApi.reducerPath,
  tableViewsApi.reducerPath,
  adminApi.reducerPath,
  ocApi.reducerPath,
  invoiceApi.reducerPath,
  associatedCampaignMarginApi.reducerPath,
  clientRegistryApi.reducerPath,
  commercialProposalApi.reducerPath,
  commercialDashboardApi.reducerPath,
  costAnalysisApi.reducerPath,
  controlBoardApi.reducerPath,
  mcpApi.reducerPath,
  serviceBriefingApi.reducerPath,
  clientPortalApi.reducerPath,
];

const migrations = {
  1: (state: RootState) => {
    console.debug("Executing migration 1");
    return {
      ...state,
    };
  },
  2: (state: RootState) => {
    console.debug("Executing migration 2: Purging stale RTK Query slices");
    const newState = { ...state };
    blacklistedPaths.forEach((key) => {
      delete newState[key];
    });
    return newState;
  },
};

const persistConfig = {
  key: "root",
  storage,
  blacklist: blacklistedPaths,
  version: 2,
  migrate: createMigrate(migrations as any, { debug: false }),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Create the saga middleware
const sagaMiddleware = createSagaMiddleware();

// Configure the store
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
      },
    })
      .concat(usersApi.middleware)
      .concat(companyApi.middleware)
      .concat(campaignApi.middleware)
      .concat(metricsApi.middleware)
      .concat(internalCampaignsMetricsApi.middleware)
      .concat(internalCampaignsMergeApi.middleware)
      .concat(systemStatsApi.middleware)
      .concat(sagaMiddleware)
      .concat(creativeMetricsApi.middleware)
      .concat(publisherMetricsApi.middleware)
      .concat(dimensionMetricsApi.middleware)
      .concat(currencyApi.middleware)
      .concat(internalCampaignApi.middleware)
      .concat(customEntitiesApi.middleware)
      .concat(insertionOrderApi.middleware)
      .concat(insertionOrderMarginApi.middleware)
      .concat(spotifyApi.middleware)
      .concat(dv360Api.middleware)
      .concat(hivestackApi.middleware)
      .concat(amazonApi.middleware)
      .concat(asyncOperationsApi.middleware)
      .concat(briefingApi.middleware)
      .concat(dataModelApi.middleware)
      .concat(wipApi.middleware)
      .concat(tableViewsApi.middleware)
      .concat(adminApi.middleware)
      .concat(ocApi.middleware)
      .concat(invoiceApi.middleware)
      .concat(associatedCampaignMarginApi.middleware)
      .concat(clientRegistryApi.middleware)
      .concat(commercialProposalApi.middleware)
      .concat(commercialDashboardApi.middleware)
      .concat(costAnalysisApi.middleware)
      .concat(controlBoardApi.middleware)
      .concat(mcpApi.middleware)
      .concat(serviceBriefingApi.middleware)
      .concat(clientPortalApi.middleware),
});

// Run the root saga
sagaMiddleware.run(rootSaga);

// Configure persistor
export const persistor = persistStore(store);

// RootState is now imported from rootReducers.ts
export type AppDispatch = typeof store.dispatch;
export default store;
